'use client';

import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Code, Upload, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonToCsvContent } from '../../data/json-to-csv-content';
import Link from 'next/link';

const sampleJson = `[
  {
    "name": "John Doe",
    "age": 30,
    "city": "New York",
    "isActive": true,
    "skills": ["JavaScript", "TypeScript", "React"]
  },
  {
    "name": "Jane Smith",
    "age": 25,
    "city": "San Francisco",
    "isActive": false,
    "skills": ["Python", "Data Analysis"]
  },
  {
    "name": "Bob Johnson",
    "age": 35,
    "city": "Chicago",
    "isActive": true,
    "skills": ["Java", "Spring", "SQL"]
  }
]`;

const sampleCsv = `name,age,city,isActive,skills
"John Doe",30,New York,true,"JavaScript, TypeScript, React"
"Jane Smith",25,San Francisco,false,"Python, Data Analysis"
"Bob Johnson",35,Chicago,true,"Java, Spring, SQL"`;

function formatJson(jsonString: string): string {
    try {
        const obj = JSON.parse(jsonString);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return jsonString;
    }
}

function convertJsonToCsv(jsonString: string, delimiter = ','): string {
    try {
        if (!jsonString.trim()) {
            return '';
        }

        // Parse JSON string to JavaScript object/array
        let jsonData;
        try {
            jsonData = JSON.parse(jsonString);
        } catch (e) {
            throw new Error('Invalid JSON format');
        }

        // If it's not an array, wrap it in an array
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];

        // Convert to CSV using PapaParse
        return Papa.unparse(dataArray, {
            delimiter: delimiter,
            header: true,
            quotes: true,
            skipEmptyLines: true,
        });
    } catch (error) {
        throw new Error(`Error converting JSON to CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

export default function JsonToCsvTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPretty, setIsPretty] = useState(true);
    const [delimiter, setDelimiter] = useState(',');
    const [fileName, setFileName] = useState<string | null>(null);
    const { toast } = useToast();

    const convertJson = useCallback((jsonString: string) => {
        try {
            if (!jsonString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            const csvString = convertJsonToCsv(jsonString, delimiter);
            setOutput(csvString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
            setOutput('');
        }
    }, [delimiter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertJson(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, delimiter, convertJson]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The CSV has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'converted_data.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleJson);
        setFileName('sample.json');
    };

    const toggleJsonFormatting = () => {
        if (input) {
            try {
                if (isPretty) {
                    // Minify JSON
                    const minified = JSON.stringify(JSON.parse(input));
                    setInput(minified);
                } else {
                    // Pretty print JSON
                    const pretty = formatJson(input);
                    setInput(pretty);
                }
                setIsPretty(!isPretty);
            } catch (e) {
                // Ignore errors
            }
        }
    };

    const handleDelimiterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setDelimiter(e.target.value);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="JSON to CSV Converter">
                Convert JSON data to CSV format instantly with our free online converter. Perfect for data export, spreadsheet import, and data analysis.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="flex-1 sm:flex-none">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Delimiter
                            </label>
                            <select
                                value={delimiter}
                                onChange={handleDelimiterChange}
                                className="w-full sm:w-24 bg-background border border-input rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/50 focus:outline-none"
                            >
                                <option value=",">, (Comma)</option>
                                <option value="\t">\t (Tab)</option>
                                <option value=";">; (Semicolon)</option>
                                <option value="|">| (Pipe)</option>
                            </select>
                        </div>

                        <div className="relative flex-1">
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                Upload JSON File
                            </label>
                            <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                                <Upload className="h-4 w-4 mr-2" />
                                <span className="text-sm truncate max-w-[200px]">
                                    {fileName || 'Choose a file...'}
                                </span>
                                <input
                                    type="file"
                                    accept=".json,application/json"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                />
                            </label>
                            {fileName && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileName(null);
                                        setInput('');
                                    }}
                                    className="absolute right-2 top-8 p-1 text-muted-foreground hover:text-foreground"
                                    title="Remove file"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your JSON data here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={toggleJsonFormatting}
                                        disabled={!input}
                                        className={cn(
                                            'gap-1.5',
                                            isPretty ? 'bg-primary/5' : ''
                                        )}
                                    >
                                        {isPretty ? (
                                            <FileText className="h-3.5 w-3.5" />
                                        ) : (
                                            <Code className="h-3.5 w-3.5" />
                                        )}
                                        {isPretty ? 'Minify' : 'Beautify'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your JSON here...\n' +
                                        '[\n' +
                                        '  {\"name\":\"John\",\"age\":30},\n' +
                                        '  {\"name\":\"Jane\",\"age\":25}\n' +
                                        ']'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        CSV Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Your converted CSV will appear here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output}
                                        className="gap-1.5"
                                    >
                                        <FileText className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your converted CSV will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted CSV will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={jsonToCsvContent.title}
                    description={jsonToCsvContent.description}
                    content={jsonToCsvContent.content}
                    faqs={jsonToCsvContent.faqs}
                    relatedTools={jsonToCsvContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
